function normalizeReportId(reportOrId) {
  const reportId = typeof reportOrId === 'object' && reportOrId !== null
    ? reportOrId.reportId ?? reportOrId.id
    : reportOrId

  if (reportId === undefined || reportId === null || String(reportId).trim() === '') {
    return null
  }

  return String(reportId)
}

function normalizePhase(phase) {
  const normalizedPhase = String(phase || '').trim().toUpperCase()
  return normalizedPhase || null
}

/**
 * Compare report ids without relying on the API's number or string shape.
 * @param {number|string} reportId - Captured report id.
 * @param {Object|number|string} reportOrId - Current report detail or its id.
 * @returns {boolean} Whether both values identify the same report.
 */
export function isSameWorkReport(reportId, reportOrId) {
  const left = normalizeReportId(reportId)
  const right = normalizeReportId(reportOrId)
  return left !== null && left === right
}

/**
 * Detect an optimistic-lock conflict returned by Axios or a normalized error.
 * @param {Object} error - Request error.
 * @returns {boolean} Whether the request failed with an HTTP 409 or version-conflict message.
 */
export function isWorkReportVersionConflict(error) {
  return error?.response?.status === 409 ||
    error?.status === 409 ||
    /^work report version has changed$/i.test(String(error?.message || '').trim())
}

/**
 * Serialize all detail write operations so evaluation and supplement requests
 * cannot use the same snapshot concurrently.
 * @returns {Object} Detail operation lock controls.
 */
export function createWorkReportDetailOperationGuard() {
  let activeOperation = null

  function acquire(reportId, phase) {
    const normalizedReportId = normalizeReportId(reportId)
    const normalizedPhase = normalizePhase(phase)
    if (activeOperation || !normalizedReportId || !normalizedPhase) {
      return null
    }

    activeOperation = {
      reportId: normalizedReportId,
      phase: normalizedPhase
    }
    return activeOperation
  }

  function release(operation) {
    if (operation === activeOperation) {
      activeOperation = null
    }
  }

  function isLocked() {
    return activeOperation !== null
  }

  return {
    acquire,
    release,
    isLocked
  }
}
